import type {
  ConditionConfiguration,
  MeasureDateRange,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import { pluralize } from "@entrilia/shared/utilities/stringHelper.ts";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type {
  AggregationDescriptor,
  ChartOfAccountsItem,
  DimensionDescriptor,
  DimensionField,
  MeasureDescriptor,
  MeasureField,
  StatementLineType,
} from "../../../../../../../../api/types/svTypes.ts";
import { AreaItemType, ItemDataType, MetaItemType, ReportType } from "../../../../../../../../api/types/svTypes.ts";
import type { StatementAxisState, StatementLineState } from "../../../../store/types/fieldsState.types.ts";
import { isAllowedInConditions } from "../../../../utilities/conditionDataTypes.ts";
import { dateRangeToShortString } from "../../../../utilities/dateRangeToShortString.ts";
import { DimensionNames } from "../../../../utilities/DimensionNames.ts";
import { isDimension } from "../../../../utilities/fieldsHelper.ts";
import type { GetFieldCaption } from "../../../viewer/financialStatement/statementGridModel.ts";
import { getLineLabel } from "../../../viewer/financialStatement/statementGridModel.ts";
import { createRowField } from "../../common/utilities/createFields.ts";
import { MeasureUtils } from "../../utils/MeasureUtils.ts";
import { getRangeText } from "./statementOptions.ts";
import type { InsertLineAnchor, LineDropTarget } from "./statementLines.ts";
import { MAX_LEVEL } from "./statementLines.ts";
import { getUniqueLabel } from "./uniqueLabel.ts";

const AMOUNT_MEASURE_NAME = "Amount";
const MEMO_AMOUNT_MEASURE_NAME = "MemoAmount";
export const STATEMENT_LINE_ITEM_TYPE = "STATEMENT_LINE";
export const STATEMENT_ACCOUNT_CATEGORY_ITEM_TYPE = "STATEMENT_ACCOUNT_CATEGORY";

const DATE_DIMENSION_REFUSAL = "Nothing to add to Rows — date dimensions belong in Columns.";
const AGGREGATION_REFUSAL = "Aggregations belong in a tabular report, not in a statement line.";

export const rowsAreaItemTypes: string[] = [
  AreaItemType.MEASURE,
  AreaItemType.DIMENSION,
  AreaItemType.AGGREGATION,
  AreaItemType.ACCOUNT,
  STATEMENT_ACCOUNT_CATEGORY_ITEM_TYPE,
  STATEMENT_LINE_ITEM_TYPE,
];

export interface StatementLineDragItem {
  lineGuid: string;
}

export type StatementAccountLedger = "Gl" | "Memo";

export type StatementAccount = Pick<ChartOfAccountsItem, "accountNo" | "accountName"> & {
  ledger?: StatementAccountLedger;
};

export interface StatementAccountCategory {
  caption: string;
  accounts: StatementAccount[];
}

export type StatementFieldDragItem =
  MeasureDescriptor | DimensionDescriptor | AggregationDescriptor | StatementAccount | StatementAccountCategory;

export type RowsDragItem = StatementFieldDragItem | StatementLineDragItem;

export type StatementFieldSource =
  | { kind: "measure"; measure: MeasureDescriptor }
  | { kind: "dimension"; dimension: DimensionDescriptor }
  | { kind: "account"; account: StatementAccount }
  | { kind: "accountCategory"; category: StatementAccountCategory }
  | { kind: "aggregation" };

export type AddToSource =
  | Exclude<StatementFieldSource, { kind: "aggregation" }>
  | { kind: "measureGroup"; caption: string; measures: MeasureDescriptor[] }
  | { kind: "dimensionGroup"; caption: string; dimensions: DimensionDescriptor[] };

export type AddToTarget = "Rows" | "Columns" | "Conditions";

export interface RowsContext {
  axes: Pick<StatementAxisState, "field">[];
  amountMeasure?: MeasureDescriptor;
  memoAmountMeasure?: MeasureDescriptor;
}

export type RowsFieldOutcome = { kind: "refuse"; message: string } | { kind: "add"; lines: StatementLineState[] };

export type RowsDropOutcome =
  | { kind: "refuse"; message: string }
  | { kind: "add"; lines: StatementLineState[]; anchor: LineDropTarget }
  | { kind: "move"; lineGuid: string; target: LineDropTarget }
  | { kind: "none" };

export type MenuLineType = Exclude<StatementLineType, "Amount" | "Dimension">;

const menuLineLabels: Record<MenuLineType, string | undefined> = {
  Group: "New Group",
  Subtotal: "Subtotal",
  Total: "Total",
  Formula: "Calculated Line",
  Spacer: undefined,
};

const lineTypeHints: Record<Exclude<StatementLineType, "Amount" | "Total">, string> = {
  Dimension: "Dimension",
  Group: "Group",
  Subtotal: "Subtotal",
  Formula: "Formula",
  Spacer: "Spacer",
};

export const findAmountMeasure = (measures: MeasureDescriptor[]): MeasureDescriptor | undefined =>
  measures.find((measure) => measure.name === AMOUNT_MEASURE_NAME);

export const findMemoAmountMeasure = (measures: MeasureDescriptor[]): MeasureDescriptor | undefined =>
  measures.find((measure) => measure.name === MEMO_AMOUNT_MEASURE_NAME);

export const getNewLineAnchor = (selectedLineId: string | undefined): InsertLineAnchor =>
  selectedLineId ? { afterGuid: selectedLineId } : "end";

const createStatementLine = (
  lineType: StatementLineType,
  overrides?: Partial<StatementLineState>
): StatementLineState => ({
  guid: generateGuid(),
  lineType,
  level: 0,
  grandTotalStyle: false,
  showAsGroup: true,
  showSubtotal: true,
  lineIds: [],
  formulaParameters: [],
  blankIfMissingValue: false,
  ...overrides,
});

const createLineMeasureField = (measure: MeasureDescriptor): MeasureField =>
  MeasureUtils.createMeasureField(structuredClone(measure), AreaItemType.VALUES);

export const createAmountLine = (measure: MeasureDescriptor): StatementLineState =>
  createStatementLine("Amount", { field: createLineMeasureField(measure) });

const createLineDimension = (dimension: DimensionDescriptor): DimensionField | undefined => {
  const dimensionField = createRowField(dimension);
  return isDimension(dimensionField) ? dimensionField : undefined;
};

const createDimensionLine = (dimension: DimensionDescriptor, amountMeasure: MeasureDescriptor): StatementLineState =>
  createStatementLine("Dimension", {
    field: createLineMeasureField(amountMeasure),
    dimension: createLineDimension(dimension),
  });

const createAccountLine = (account: StatementAccount, measureMeta: MeasureDescriptor): StatementLineState => {
  const field = createLineMeasureField(measureMeta);
  const accountCondition: ConditionConfiguration = {
    guid: generateGuid(),
    filter: {
      dimensionName: DimensionNames.AccountNo,
      values: [account.accountNo],
      equalityType: ConditionEqualityType.Equal,
    },
  };
  return createStatementLine("Amount", {
    customLabel: account.accountName,
    field: {
      ...field,
      config: {
        ...field.config,
        useCustomConditions: true,
        customConditions: [...(field.config.customConditions ?? []), accountCondition],
      },
    },
  });
};

export const createMenuLine = (lineType: MenuLineType, existingLabels: string[]): StatementLineState => {
  const label = menuLineLabels[lineType];
  return createStatementLine(lineType, { customLabel: label && getUniqueLabel(label, existingLabels) });
};

const createGroupLines = (caption: string, members: StatementLineState[]): StatementLineState[] => [
  createStatementLine("Group", { customLabel: caption }),
  ...members.map((line) => ({ ...line, level: 1 })),
  createStatementLine("Subtotal", { customLabel: `Total ${caption}` }),
];

export const createMeasureGroupLines = (caption: string, measures: MeasureDescriptor[]): StatementLineState[] =>
  createGroupLines(caption, measures.map(createAmountLine));

const isAxisDimension = (dimension: DimensionDescriptor, axes: Pick<StatementAxisState, "field">[]): boolean =>
  axes.some((axis) => axis.field.config.name === dimension.name);

const refuseWithoutAmountMeasure = (caption: string, measureName = AMOUNT_MEASURE_NAME): RowsFieldOutcome => ({
  kind: "refuse",
  message: `${caption} can't be added — this data set has no ${measureName} measure.`,
});

const getAccountMeasure = (account: StatementAccount, context: RowsContext): MeasureDescriptor | undefined =>
  account.ledger === "Memo" ? context.memoAmountMeasure : context.amountMeasure;

const refuseWithoutAccountMeasure = (caption: string, account: StatementAccount): RowsFieldOutcome =>
  refuseWithoutAmountMeasure(caption, account.ledger === "Memo" ? "Memo Amount" : AMOUNT_MEASURE_NAME);

const resolveAccountCategory = (category: StatementAccountCategory, context: RowsContext): RowsFieldOutcome => {
  const members: StatementLineState[] = [];
  for (const account of category.accounts) {
    const measure = getAccountMeasure(account, context);
    if (!measure) {
      return refuseWithoutAccountMeasure(category.caption, account);
    }
    members.push(createAccountLine(account, measure));
  }
  return { kind: "add", lines: createGroupLines(category.caption, members) };
};

export const resolveFieldForRows = (source: StatementFieldSource, context: RowsContext): RowsFieldOutcome => {
  switch (source.kind) {
    case "measure":
      return { kind: "add", lines: [createAmountLine(source.measure)] };
    case "account": {
      const measure = getAccountMeasure(source.account, context);
      return measure
        ? { kind: "add", lines: [createAccountLine(source.account, measure)] }
        : refuseWithoutAccountMeasure(source.account.accountName, source.account);
    }
    case "accountCategory":
      return resolveAccountCategory(source.category, context);
    case "aggregation":
      return { kind: "refuse", message: AGGREGATION_REFUSAL };
    case "dimension":
      if (source.dimension.type === ItemDataType.Date) {
        return { kind: "refuse", message: DATE_DIMENSION_REFUSAL };
      }
      if (isAxisDimension(source.dimension, context.axes)) {
        return { kind: "refuse", message: `${source.dimension.caption} already slices the columns.` };
      }
      if (!context.amountMeasure) {
        return refuseWithoutAmountMeasure(source.dimension.caption);
      }
      return { kind: "add", lines: [createDimensionLine(source.dimension, context.amountMeasure)] };
  }
};

export const resolveDimensionGroupForRows = (
  caption: string,
  dimensions: DimensionDescriptor[],
  context: RowsContext
): RowsFieldOutcome => {
  if (!context.amountMeasure) {
    return refuseWithoutAmountMeasure(caption);
  }
  const outcomes = dimensions.map((dimension) => resolveFieldForRows({ kind: "dimension", dimension }, context));
  const members = outcomes.flatMap((outcome) => (outcome.kind === "add" ? outcome.lines : []));
  if (members.length === 0) {
    return outcomes.find((outcome) => outcome.kind === "refuse") ?? { kind: "refuse", message: DATE_DIMENSION_REFUSAL };
  }
  return { kind: "add", lines: createGroupLines(caption, members) };
};

export const toStatementFieldSource = (item: StatementFieldDragItem): StatementFieldSource => {
  if ("accounts" in item) {
    return { kind: "accountCategory", category: item };
  }
  if ("accountNo" in item) {
    return { kind: "account", account: item };
  }
  switch (item.itemType) {
    case MetaItemType.MEASURE:
      return { kind: "measure", measure: item };
    case MetaItemType.DIMENSION:
      return { kind: "dimension", dimension: item };
    case MetaItemType.AGGREGATION:
      return { kind: "aggregation" };
  }
};

const toRowsDropSource = (item: RowsDragItem): StatementFieldSource | { kind: "line"; lineGuid: string } =>
  "lineGuid" in item ? { kind: "line", lineGuid: item.lineGuid } : toStatementFieldSource(item);

export const resolveRowsDrop = (item: RowsDragItem, target: LineDropTarget, context: RowsContext): RowsDropOutcome => {
  const source = toRowsDropSource(item);
  if (source.kind === "line") {
    const isSelfDrop = target !== "end" && target.beforeGuid === source.lineGuid;
    return isSelfDrop ? { kind: "none" } : { kind: "move", lineGuid: source.lineGuid, target };
  }
  const outcome = resolveFieldForRows(source, context);
  return outcome.kind === "add" ? { ...outcome, anchor: target } : outcome;
};

const breakDownLineTypes: StatementLineType[] = ["Amount", "Dimension", "Group"];

export const getBreakDownDimension = (
  item: RowsDragItem,
  lineType: StatementLineType
): DimensionDescriptor | undefined => {
  const source = toRowsDropSource(item);
  const isBreakDown =
    source.kind === "dimension" && source.dimension.type !== ItemDataType.Date && breakDownLineTypes.includes(lineType);
  return isBreakDown ? source.dimension : undefined;
};

export interface BreakDownContext {
  axes: Pick<StatementAxisState, "field">[];
  getLabel: (line: StatementLineState) => string;
}

export type BreakDownOutcome =
  | { kind: "refuse"; message: string }
  | { kind: "apply"; lines: StatementLineState[]; selectedGuid: string; message: string };

const refuseBreakDown = (message: string): BreakDownOutcome => ({ kind: "refuse", message });

const canBreakDown = (line: StatementLineState): boolean =>
  line.lineType === "Dimension" || (line.lineType === "Amount" && line.field !== undefined);

const isBrokenDownBy = (line: StatementLineState, dimension: DimensionDescriptor): boolean =>
  line.lineType === "Dimension" && line.dimension?.config.name === dimension.name;

const getBreakDownCandidates = (lines: StatementLineState[], index: number): StatementLineState[] => {
  const target = defined(lines[index]);
  if (target.lineType !== "Group") {
    return canBreakDown(target) ? [target] : [];
  }
  const end = lines.findIndex((line, lineIndex) => lineIndex > index && line.level <= target.level);
  return lines.slice(index + 1, end === -1 ? lines.length : end).filter(canBreakDown);
};

const breakDownAmount = (
  line: StatementLineState,
  label: string,
  dimension: DimensionDescriptor
): StatementLineState[] => [
  createStatementLine("Group", { customLabel: label, level: line.level }),
  createStatementLine("Dimension", {
    level: line.level + 1,
    field: line.field,
    dateRange: line.dateRange,
    format: line.format,
    dimension: createLineDimension(dimension),
    showAsGroup: false,
    showSubtotal: false,
  }),
  createStatementLine("Subtotal", {
    guid: line.guid,
    level: line.level,
    customLabel: `Total ${label}`,
    format: line.format,
  }),
];

const getBreakDownMessage = (
  target: StatementLineState,
  label: string,
  changedCount: number,
  caption: string
): string => {
  switch (target.lineType) {
    case "Group":
      return `${changedCount} ${pluralize("line", changedCount)} under ${label} broken down by ${caption}.`;
    case "Amount":
      return `${label} broken down by ${caption} with a subtotal.`;
    default:
      return `${label} broken down by ${caption}.`;
  }
};

const quoteLabels = (lines: StatementLineState[], getLabel: BreakDownContext["getLabel"]): string =>
  lines.map((line) => `“${getLabel(line)}”`).join(", ");

export const breakDownLine = (
  lines: StatementLineState[],
  guid: string,
  dimension: DimensionDescriptor,
  context: BreakDownContext
): BreakDownOutcome => {
  const index = lines.findIndex((line) => line.guid === guid);
  const target = defined(lines[index]);
  const targetLabel = context.getLabel(target);

  if (dimension.type === ItemDataType.Date) {
    return refuseBreakDown(`${dimension.caption} is a date dimension — drop it into Columns.`);
  }
  if (isAxisDimension(dimension, context.axes)) {
    return refuseBreakDown(`${dimension.caption} already slices the columns.`);
  }

  const candidates = getBreakDownCandidates(lines, index);
  if (candidates.length === 0) {
    return refuseBreakDown(`${targetLabel} has no amount lines to break down.`);
  }
  const pending = candidates.filter((line) => !isBrokenDownBy(line, dimension));
  if (pending.length === 0) {
    return refuseBreakDown(`${targetLabel} is already broken down by ${dimension.caption}.`);
  }
  const tooDeep = pending.filter((line) => line.lineType === "Amount" && line.level >= MAX_LEVEL);
  const changed = pending.filter((line) => !tooDeep.includes(line));
  if (changed.length === 0) {
    return refuseBreakDown(
      `Nothing can be nested below the deepest level, so ${quoteLabels(tooDeep, context.getLabel)} can't be broken down.`
    );
  }

  const brokenDown = lines.flatMap((line) => {
    if (!changed.includes(line)) {
      return [line];
    }
    return line.lineType === "Dimension"
      ? [{ ...line, dimension: createLineDimension(dimension), unresolvedDimension: undefined }]
      : breakDownAmount(line, context.getLabel(line), dimension);
  });
  const skipped =
    tooDeep.length > 0
      ? ` Not broken down, as there is no deeper level to nest them in: ${quoteLabels(tooDeep, context.getLabel)}.`
      : "";
  return {
    kind: "apply",
    lines: brokenDown,
    selectedGuid: defined(brokenDown[index]).guid,
    message: getBreakDownMessage(target, targetLabel, changed.length, dimension.caption) + skipped,
  };
};

export const getAddToTargets = (source: AddToSource): AddToTarget[] => {
  switch (source.kind) {
    case "measure":
    case "account":
      return ["Rows"];
    case "accountCategory":
      return source.category.accounts.length > 0 ? ["Rows"] : [];
    case "measureGroup":
      return source.measures.length > 0 ? ["Rows"] : [];
    case "dimensionGroup": {
      const rows: AddToTarget[] = source.dimensions.some((dimension) => dimension.type !== ItemDataType.Date)
        ? ["Rows"]
        : [];
      return source.dimensions.length > 0 ? [...rows, "Columns"] : rows;
    }
    case "dimension": {
      const rows: AddToTarget[] = source.dimension.type === ItemDataType.Date ? [] : ["Rows"];
      const areas: AddToTarget[] = [...rows, "Columns"];
      return isAllowedInConditions(source.dimension, ReportType.FinancialStatement) ? [...areas, "Conditions"] : areas;
    }
  }
};

export const getLineHint = (
  line: Pick<StatementLineState, "guid" | "lineType" | "lineIds">,
  periods: Record<string, MeasureDateRange>
): string => {
  if (line.lineType === "Amount") {
    return dateRangeToShortString(periods[line.guid]);
  }
  if (line.lineType === "Total") {
    return `${line.lineIds.length} ${pluralize("line", line.lineIds.length)}`;
  }
  return lineTypeHints[line.lineType];
};

export const getLineHintTooltip = (
  line: Pick<StatementLineState, "guid" | "lineType" | "lineIds" | "showAsGroup" | "dimension">,
  periods: Record<string, MeasureDateRange>,
  getFieldCaption: GetFieldCaption
): string | undefined => {
  switch (line.lineType) {
    case "Amount": {
      const period = periods[line.guid];
      return period === undefined ? undefined : `Period — ${getRangeText(period)}`;
    }
    case "Dimension": {
      if (!line.dimension) {
        return undefined;
      }
      const layout = line.showAsGroup ? "shown as a group" : "shown flat";
      return `Dimension from ${getFieldCaption(line.dimension.config.name)}, ${layout}`;
    }
    case "Group":
      return "Group heading — the lines nested under it";
    case "Subtotal":
      return "Sums the amount and dimension lines nested under it";
    case "Total":
      return `Sums ${getLineHint(line, periods)}`;
    case "Formula":
    case "Spacer":
      return undefined;
  }
};

export const getStatementLineLabel = (line: StatementLineState, getFieldCaption: GetFieldCaption): string =>
  line.lineType === "Spacer"
    ? ""
    : getLineLabel(
        {
          ...line,
          field: line.field?.config ?? line.unresolvedField,
          dimension: line.dimension?.config ?? line.unresolvedDimension,
        },
        getFieldCaption
      );

export const getUnresolvedLineWarning = (
  line: Pick<StatementLineState, "unresolvedField" | "unresolvedDimension">
): string | undefined => {
  if (line.unresolvedField) {
    return `The measure “${line.unresolvedField.name}” no longer exists. Remove this line.`;
  }
  if (line.unresolvedDimension) {
    return `The dimension “${line.unresolvedDimension.name}” no longer exists. Remove this line.`;
  }
  return undefined;
};

export const getLineDefaultLabel = (line: StatementLineState, getFieldCaption: GetFieldCaption): string => {
  switch (line.lineType) {
    case "Amount":
    case "Dimension":
      return getStatementLineLabel({ ...line, customLabel: undefined }, getFieldCaption);
    default:
      return menuLineLabels[line.lineType] ?? "";
  }
};
